import { REQUEST_TIMEOUT_MS } from '../dataProvider'

const apiUrl = import.meta.env.VITE_BACKEND_API_URL

// Authorization header only when a sessionStorage token exists. On a
// cookie-only session (see below) there is no token to attach and the header
// is omitted — the same-origin HttpOnly `payload-token` cookie authenticates
// the request instead, exactly like the dataProvider's token-less /api calls.
//
// Uses the `JWT` Authorization prefix to match the dataProvider's httpClient
// (and ada-core/CLAUDE.md's convention) instead of `Bearer`. Both work
// server-side but mixing them muddies log greps.
export const buildAuthHeaders = (token: string | null): HeadersInit => {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `JWT ${token}`
  return headers
}

/**
 * One fetch with a DEADLINE. react-query imposes no timeout of its own, so
 * without this a backend that accepts the connection and never answers leaves
 * the promise pending forever — and `PetList` / `EnhancedDeviceDatagrid` gate
 * their entire render on this hook's `isLoading`, so the page spins with no
 * error and no way out but a manual refresh. (PetShow already carries a 10s
 * watchdog naming exactly this failure; the other two never got one.)
 *
 * The deadline value is imported from the dataProvider rather than re-declared,
 * so the app has ONE answer to "how long do we wait for the backend" instead of
 * a third number that drifts.
 *
 * The caller's signal (react-query's cancellation on unmount) still aborts
 * immediately — the deadline is an upper bound, never a floor.
 */
const fetchWithDeadline = (url: string, signal?: AbortSignal): Promise<Response> =>
  deadlineFetch(url, {
    headers: buildAuthHeaders(sessionStorage.getItem('token')),
    credentials: 'same-origin',
    signal,
  })

/**
 * Transport-only deadline wrapper for the call sites that deliberately live
 * OUTSIDE the dataProvider (the L-044 class: vet-shares dialogs, invite
 * acceptance, client-profile PATCH, …). No auth, no JSON parsing, no retry —
 * its one job is that a backend which ACCEPTS the connection and never
 * answers becomes an ordinary AbortError rejection within REQUEST_TIMEOUT_MS,
 * so the caller's existing catch/finally runs instead of a `saving`/`busy`
 * flag sticking true forever (which is how EditClientDialog and the
 * invite-accept page used to become un-escapable).
 *
 * The caller's own signal (unmount cancellation) still aborts immediately —
 * the deadline is an upper bound, never a floor.
 */
export const deadlineFetch = (
  url: string,
  init: RequestInit = {},
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<Response> => {
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort()
  const callerSignal = init.signal ?? undefined
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort()
    else callerSignal.addEventListener('abort', abortFromCaller, { once: true })
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  return fetch(url, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timer)
    callerSignal?.removeEventListener('abort', abortFromCaller)
  })
}

/**
 * GETs an ada-core custom endpoint with retry-once-on-401 semantics matching
 * the React Admin dataProvider's httpClient. Shared by every custom-endpoint
 * hook so these auth semantics cannot drift between them — they were expensive
 * to get right and are re-derived below.
 *
 * Auth via cookie OR token — NOT token-only. `sessionStorage` is per-tab and is
 * wiped when the browser closes, but the `payload-token` cookie is persistent,
 * so a reopened browser auto-logs-in via the cookie with an EMPTY
 * sessionStorage (no token). These requests must authenticate the same way the
 * rest of the app does — they are same-origin (`/api/...`), so the cookie rides
 * along automatically; the JWT header is attached only as a bonus when a token
 * happens to be present.
 *
 * REGRESSION THIS ENCODES (prod 2026-07-13, pet 285673 / Olivia, a run of
 * after-hours "the chart is gone" support calls): a prior version bailed to
 * `null` the instant `sessionStorage.token` was missing — to silence 401
 * log-noise during the pre-auth mount race. But on EVERY browser reopen the
 * token is missing while the cookie is valid, so the request never fired, the
 * hook never settled, and PetShow's `dataStatusSettled` gate hung → the entire
 * glucose panel + status chip silently vanished even though devices + 39k
 * metrics were loading fine over the cookie. Clearing cache (which also wipes
 * the cookie → forces a fresh login → repopulates the token) "fixed" it until
 * the next reopen. Firing the request cookie-first ends that loop. The 401
 * log-noise the bail avoided does not return in practice: these hooks only
 * mount behind the auth gate (PetShow/Dashboard), where a session — cookie or
 * token — already exists; a truly anonymous hit just 401s → retry → degrade,
 * bounded by react-query's dedup + staleTime.
 *
 * Returns null only when the retry was aborted mid-flight.
 */
export const fetchCustomEndpointOnce = async (
  path: string,
  signal?: AbortSignal,
): Promise<Response | null> => {
  const url = `${apiUrl}${path}`
  // `same-origin` sends the HttpOnly cookie for the relative /api origin; a
  // token, when present, is attached as the Authorization header too.
  const first = await fetchWithDeadline(url, signal)
  if (first.status !== 401) {
    return first
  }
  // Quiet retry-once: pause briefly to let any sibling request refresh the
  // token, then re-attach the latest token from sessionStorage and try again.
  await new Promise((resolve) => setTimeout(resolve, 300))
  if (signal?.aborted) return null
  // A FRESH deadline for the retry — sharing the first one would give the
  // second attempt whatever time the first left over, which is arbitrary.
  return fetchWithDeadline(url, signal)
}
